import type React from "react"
import { useTranslation } from "react-i18next"
import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  AppBar,
  Toolbar,
} from "@mui/material"
import { ArrowBack as ArrowBackIcon, Fullscreen as FullscreenIcon, Refresh as RefreshIcon, VideocamOff as VideocamOffIcon } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import type { Camera } from "../interfaces"
import HLSPlayer from "./HLSPlayer"
import { streamService } from "../services/streamService"

export interface StreamDialogProps {
  open: boolean
  onClose: () => void
  camera: Camera | null
  loading: boolean
  tenant_id?: number
  streamUrl: string | null
  errorMessage?: string | null
  onClearError?: () => void
  onStreamReady?: (url: string) => void
}

const getTenantId = (): number => {
  try {
    const userStr = localStorage.getItem("user")
    if (userStr) {
      const user = JSON.parse(userStr)
      const userTenant = user?.userTenant
      if (userTenant) return Number(userTenant)
    }
  } catch (e) {
    console.error("Erro ao buscar tenant do usuário:", e)
  }

  const selectedTenantId = localStorage.getItem("selectedTenantId")
  if (selectedTenantId) return Number(selectedTenantId)

  return 1
}

const getFriendlyErrorMessage = (error: any, fallback: string): string => {
  return error?.message || fallback
}

const StreamDialog: React.FC<StreamDialogProps> = ({
  open,
  onClose,
  camera,
  loading: initialLoading,
  tenant_id,
  streamUrl: initialStreamUrl,
  errorMessage,
  onClearError,
  onStreamReady,
}) => {
  const { t } = useTranslation()
  const activeTenantId = tenant_id ?? getTenantId()

  const [streamUrl, setStreamUrl] = useState<string | null>(initialStreamUrl)
  const [loading, setLoading] = useState<boolean>(initialLoading)
  const [error, setError] = useState<string | null>(null)
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false)

  useEffect(() => {
    setLoading(initialLoading)
  }, [initialLoading])

  useEffect(() => {
    setStreamUrl(initialStreamUrl)

    if (initialStreamUrl) {
      setLoading(false)
      setError(null)
      onClearError?.()
    }
  }, [initialStreamUrl, onClearError])

  useEffect(() => {
    if (open) {
      setError(null)
    }
  }, [open, camera?.cameraId])

  const handlePlayerError = (err: any) => {
    console.error("Erro no player:", err)
    setLoading(false)
    setError(t("stream.playbackError"))
  }

  const handleRefresh = async () => {
    if (!camera) return

    setLoading(true)
    setError(null)
    setStreamUrl(null)
    onClearError?.()

    try {
      console.log("🔄 StreamDialog: Reiniciando processo completo do stream:", {
        cameraId: camera.cameraId,
        tenantId: activeTenantId,
      })

      await streamService.stopStream(activeTenantId)

      const result = await streamService.prepareStreamData(camera, null, activeTenantId)

      if (!result.success || !result.url) {
        throw new Error(
          result.errorMessage || t("stream.restartStreamError")
        )
      }

      setStreamUrl(result.url)
      setError(null)
      onClearError?.()
      onStreamReady?.(result.url)

      console.log("✅ StreamDialog: Stream reiniciado com sucesso")
    } catch (err: any) {
      console.error("Erro ao reiniciar stream:", err)

      setStreamUrl(null)
      setError(getFriendlyErrorMessage(err, t("stream.refreshStreamError")))
    } finally {
      setLoading(false)
    }
  }

  const toggleFullScreen = () => {
    setIsFullScreen((prev) => !prev)
  }

  const handleClose = async () => {
  const hadStream = !!streamUrl

  setStreamUrl(null)
  setLoading(false)
  setError(null)
  onClearError?.()

  try {
    if (hadStream) {
      await streamService.stopStream(activeTenantId)
    }
  } catch (err) {
    console.error("Erro ao parar stream:", err)
  } finally {
    onClose()
  }
}

  const contentHeight = isFullScreen ? 600 : 400
  const isMobileScreen = window.innerWidth < 600
  const finalError = errorMessage || error

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobileScreen}
    >
      {isMobileScreen && (
        <AppBar position="sticky" color="primary" sx={{ mb: 2 }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
              {camera?.cameraName || t("stream.streamTitle")}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {camera && (
        <>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">{t("stream.cameraStreamTitle", { name: camera.cameraName })}</Typography>

              <Box>
                <Tooltip title={t("stream.reload")}>
                  <IconButton size="small" onClick={handleRefresh} disabled={loading} sx={{ mr: 1 }}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title={isFullScreen ? t("stream.minimize") : t("stream.maximize")}>
                  <IconButton size="small" onClick={toggleFullScreen}>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ position: "relative", mt: 1, height: contentHeight }}>
              {loading && !streamUrl && !finalError && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "rgba(0,0,0,0.7)",
                    zIndex: 10,
                    borderRadius: 1,
                    color: "white",
                  }}
                >
                  <CircularProgress color="primary" size={60} sx={{ mb: 2 }} />
                </Box>
              )}

              {open && streamUrl && !finalError && (
  <HLSPlayer
    key={streamUrl}
    streamUrl={streamUrl}
    cameraName={camera.cameraName}
    height={contentHeight}
    onError={handlePlayerError}
    externalError={finalError}
  />
)}

              {/* Estado de erro PERSISTENTE (cobre falha sem streamUrl e erro do
                  player): mensagem clara + botão de tentar novamente, em vez de
                  uma mensagem temporária + tela vazia. */}
              {finalError && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1.5,
                    px: 3,
                    textAlign: "center",
                    bgcolor: "rgba(0,0,0,0.85)",
                    color: "common.white",
                    borderRadius: 1,
                    zIndex: 20,
                  }}
                >
                  <VideocamOffIcon sx={{ fontSize: 44, color: "error.main" }} />
                  <Typography variant="body1">{finalError}</Typography>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    {t("stream.retry")}
                  </Button>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} startIcon={<ArrowBackIcon />}>
              {t("common.close")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default StreamDialog